import ClientModel from '@/models/client-model';
import {
  IPrescriptionBase,
  IPrescriptionResume,
} from '@/interfaces/prescription-base';
import PatientModel from '@/models/patient-model';
import * as prescriptionTypes from '@/settings/consts/prescriptions-types';
import {
  INFUSION_TIME_OPTIONS,
  UNIT_ML_ID,
} from '@/settings/consts/prescription-params';
import { toNumber } from '@/helpers/toNumber';
import { decimalFormat } from '@/helpers/decimalFormat';
import {
  isWaitingClient,
  isWaitingEmede,
  isWaitingTranscription,
  isWaitingPharmaceuticalApprove,
} from '@/helpers/validatePrescriptionStatus';

export default abstract class PrescriptionBaseModel {
  public id?: string;
  public cliente: ClientModel;
  public paciente: PatientModel;
  public tempoInfusao: number;
  public volumeDigitado: string;
  public volumeDesejado: string;
  public volumeFinalMlKgDia: string;
  public volumeDigitadoPorKg: boolean;
  public divisaoBolsas: number;
  public bolsaMulticompart: boolean;
  public quantidade: number;
  public justifQuantidade: string;
  public viaAdministracao: string;
  public osmolaridade: string;
  public caloriasNaoProteicas: string;
  public produtos: any[];
  public statusPrescricao: string;
  public resumo: IPrescriptionResume;
  public unidadesCliente: any[];
  public padroesTemplate: any[];
  public usoPesoCalorico: boolean;

  constructor(_initialValue?: IPrescriptionBase) {
    const initialValue = _initialValue || {
      id: null,
      cliente: new ClientModel(),
      paciente: new PatientModel(),
      tempoInfusao: null,
      volumeDigitado: null,
      volumeDesejado: null,
      volumeFinalMlKgDia: null,
      volumeDigitadoPorKg: false,
      divisaoBolsas: 1,
      bolsaMulticompart: false,
      quantidade: 1,
      justifQuantidade: null,
      viaAdministracao: null,
      osmolaridade: null,
      caloriasNaoProteicas: null,
      produtos: [],
      resumo: null,
      unidadesCliente: [],
      padroesTemplate: [],
    };

    this.id = initialValue.id;
    this.cliente = new ClientModel(initialValue.cliente);
    this.paciente = new PatientModel(initialValue.paciente);
    this.tempoInfusao = initialValue.tempoInfusao;
    this.volumeDigitado = initialValue.volumeDigitado;
    this.volumeDesejado = initialValue.volumeDesejado;
    this.volumeFinalMlKgDia = initialValue.volumeFinalMlKgDia;
    this.volumeDigitadoPorKg = initialValue.volumeDigitadoPorKg;
    this.divisaoBolsas = initialValue.divisaoBolsas;
    this.viaAdministracao = initialValue.viaAdministracao;
    this.osmolaridade = initialValue.osmolaridade;
    this.caloriasNaoProteicas = initialValue.caloriasNaoProteicas;
    this.produtos = this.formatProducts(initialValue.produtos);
    this.resumo = initialValue.resumo;
  }

  public setProducts(products): void {
    this.produtos = this.formatProducts(products);
  }

  public setClientesTemplates(unidadesCliente): void {
    this.unidadesCliente = unidadesCliente;
  }

  public setPadroesTemplates(padroesTemplate): void {
    this.padroesTemplate = padroesTemplate;
  }

  public valid(): boolean {
    return (
      !!this.cliente &&
      !!this.cliente.id &&
      !!this.paciente &&
      !!this.paciente.peso &&
      !!this.paciente.tipo &&
      !!this.divisaoBolsas &&
      !!this.viaAdministracao &&
      !!this.produtos.length
    );
  }

  public isEdit(): boolean {
    return !!this.id;
  }

  public isVisualization(): boolean {
    return false;
  }

  public isTranscription(): boolean {
    return false;
  }

  public isTranscriptionCopy(): boolean {
    return false;
  }

  public isModel(): boolean {
    return false;
  }

  public isCopy(): boolean {
    return false;
  }

  public isProfile(): boolean {
    return false;
  }

  public isPattern(): boolean {
    return false;
  }

  public isWaitingApprovation(): boolean {
    return (
      isWaitingEmede(this.statusPrescricao) ||
      isWaitingPharmaceuticalApprove(this.statusPrescricao) ||
      isWaitingTranscription(this.statusPrescricao) ||
      isWaitingClient(this.statusPrescricao)
    );
  }

  public calcTypedVolume(): void {
    if (!this.produtos.length) {
      this.volumeDigitado = '0';
    }
    const currentTotalVolume = this.produtos.reduce((total, product) => {
      const unitFound = product.unidadeMedidasProduto.find((unit) => {
        return unit.id === UNIT_ML_ID;
      });

      if (!!unitFound) {
        let sum = total + toNumber(unitFound.valor);
        sum = decimalFormat(sum);
        sum = toNumber(sum, true);
        return sum;
      } else {
        return total;
      }
    }, 0);

    this.volumeDigitado = currentTotalVolume;
  }

  public toJson(): any {
    const jsonretorno = {
      cliente: {
        id: this.cliente.id,
      },
      paciente: {
        peso: this.paciente.peso,
        termo: this.paciente.tipo,
      },
      tipoPrescricao: prescriptionTypes.DEFAULT,
      tempoInfusao: this.tempoInfusao,
      divisaoBolsas: !!this.divisaoBolsas ? this.divisaoBolsas : '1',
      viaAdministracao: !!this.viaAdministracao
        ? this.viaAdministracao
        : 'CENTRAL',
      volumeDesejado: this.volumeDesejado || this.volumeDigitado,
      produtos: this.produtos,
      osmolaridade: toNumber(this.osmolaridade),
      caloriasNaoProteicas: toNumber(this.caloriasNaoProteicas),
    };
    return jsonretorno;
  }

  protected formatProducts(products): any[] {
    if (!products) {
      return [];
    }
    return products.map((product) => {
      return {
        ...product,
        id: product.idProduto,
        unidadeMedidasProduto: product.unidades,
      };
    });
  }
}
